import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { Users as UsersIcon } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import RoleScopedPlaceholder from "../../components/RoleScopedPlaceholder";
import formatApiError from "../../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const ROLE_LABEL = {
  admin:            "Super Admin",
  state_admin:      "State Admin",
  university_admin: "University Admin",
  college_admin:    "College Admin",
  it_admin:         "IT Admin",
  support_user:     "Support User",
  faculty:          "Faculty",
};
const ROLE_COLOR = {
  admin:            { c: "#3072FF", bg: "#EEF3FF" },
  state_admin:      { c: "#7C3AED", bg: "#F5F3FF" },
  university_admin: { c: "#1A5AE2", bg: "#E8EEFF" },
  college_admin:    { c: "#059669", bg: "#ECFDF5" },
  it_admin:         { c: "#D97706", bg: "#FFFBEB" },
  support_user:     { c: "#0891B2", bg: "#ECFEFF" },
  faculty:          { c: "#BE185D", bg: "#FDF2F8" },
};

export default function PortalUsersPage() {
  const { user } = useContext(AuthContext);

  // Only college_admin has a real Users page (view-only) right now.
  // Other portal roles get the existing placeholder.
  if (user?.role !== "college_admin") {
    return <RoleScopedPlaceholder title="Users" moduleKey="users" icon={UsersIcon} />;
  }

  return <CollegeAdminUsersList />;
}

function CollegeAdminUsersList() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/api/users`, ax());
      setRows(data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load users."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-sub">All accounts associated with your college.</p>
        </div>
      </div>

      {loading ? (
        <div className="table-loading">Loading…</div>
      ) : error ? (
        <div className="table-error">{error}</div>
      ) : (
        <div className="data-card">
          {rows.length === 0 ? (
            <div className="empty-state"><UsersIcon size={32} color="#43E0FF" /><p>No users yet.</p></div>
          ) : (
            <table className="data-table" data-testid="college-users-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th style={{ width: 150 }}>Role</th>
                  <th style={{ width: 180 }}>Phone</th>
                  <th>Subject</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u, i) => {
                  const r = ROLE_COLOR[u.role] || { c: "#475569", bg: "#F1F5F9" };
                  return (
                    <tr key={u.id} data-testid={`college-user-row-${u.id}`}>
                      <td className="td-num">{i + 1}</td>
                      <td className="td-name">{u.name}</td>
                      <td className="td-desc">{u.email}</td>
                      <td>
                        <span className="td-badge" style={{ background: r.bg, color: r.c }}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="td-desc">{u.phone ? `${u.phone_code} ${u.phone}` : <span className="td-empty">—</span>}</td>
                      <td className="td-desc">
                        {u.subject_name ? (
                          <span className="subject-pill">
                            {u.subject_code && <span className="subject-pill-code">{u.subject_code}</span>}
                            {u.subject_name}
                          </span>
                        ) : <span className="td-empty">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
